import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import dayjs from 'dayjs';
import classes from './CustomBiMonthlyPicker.module.scss';
import colors from '../../../scss/colors';
import { calendarTypes } from '../../../utils/constants';
import { generalEnums } from '../../../utils/enums';

export default function CustomBiMonthlyPicker({
  dateDisablingAbility = false,
  copyFrom = false,
  daysAvailability = [],
  userDate = dayjs(),
  setUserDate,
  calendarType,
  backgroundColor = colors.WHITE,
  disabledBeforeIncluded,
  dateBackAction = () => {},
  dateForwardAction = () => {},
  sx,
  loading = false
}) {
  const [dateToShow, setDateToShow] = useState(dayjs());
  const [selectedRange, setSelectedRange] = useState(null);
  const currentDay = useMemo(() => dayjs().toDate(), []);

  const firstDayOfTheMonth = useMemo(() => dateToShow.clone().startOf('month'), [dateToShow]);

  const firstDayOfFirstWeekOfMonth = useMemo(() => {
    if (firstDayOfTheMonth.day() === 0) {
      return dayjs(firstDayOfTheMonth.add(-1, 'day')).startOf('week');
    }
    return dayjs(firstDayOfTheMonth).startOf('week');
  }, [firstDayOfTheMonth]);

  const generateFirstDayOfEachWeek = useCallback((day) => {
    const dates = [day];
    let firstDate;
    for (let i = 1; i < 6; i++) {
      const dateFD = day.clone().add(i, 'week');
      if (dateFD.get('date') === 1) {
        firstDate = dateFD;
      }
      if (
        i === 5 &&
        dateFD.add(3, 'day').month() !== dates[1].month() &&
        firstDate?.day() !== 0 &&
        firstDate?.day() !== 6
      )
        break;
      dates.push(dateFD);
    }
    return dates;
  }, []);

  const generateWeek = useCallback((day) => {
    const dates = [];
    for (let i = 1; i < 8; i++) {
      const dateW = day.clone().add(i, 'day').toDate();
      dates.push(dateW);
    }
    return dates;
  }, []);

  const generateWeeksOfTheMonth = useMemo(() => {
    const firstDayOfEachWeek = generateFirstDayOfEachWeek(firstDayOfFirstWeekOfMonth);
    return firstDayOfEachWeek.map((dateFDEW) => generateWeek(dateFDEW));
  }, [generateFirstDayOfEachWeek, firstDayOfFirstWeekOfMonth, generateWeek]);

  const getClassForVariant = (day) => {
    if (dateToShow.clone().toDate().getMonth() !== day.getMonth()) {
      return classes.nextMonth;
    }

    if (selectedRange) {
      if (day >= selectedRange.start && day <= selectedRange.end) {
        if (day.getTime() === selectedRange.start.getTime()) {
          return `${classes.selected} ${classes.firstSelection}`;
        }
        if (day.getTime() === selectedRange.end.getTime()) {
          return `${classes.selected} ${classes.lastSelection}`;
        }
        return classes.selected;
      }
    }

    return classes.default;
  };
  // TODO - Check with Firnas and remove this
  // const getClassWeekVariant = (week) => {
  //   if (calendarType === calendarTypes.WEEK) {
  //     return week.some((item) => dayjs(item.toString()).isSame(userDate, 'date'))
  //       ? `${classes.calendarWeekWrapper} ${classes.selected}`
  //       : classes.calendarWeekWrapper;
  //   }

  //   return classes.calendarDayWrapper;
  // };

  const isDisabled = (day) => (disabledBeforeIncluded ? dayjs(day).isBefore(disabledBeforeIncluded) : false);
  const isDayDisabled = (day) =>
    dateDisablingAbility && !daysAvailability.includes(dayjs(day).format(generalEnums.DATEFORMAT));

  const handlePreviousCalendar = () => {
    setDateToShow((dateBack) => {
      const date = dateBack.subtract(1, 'month');
      dateBackAction(date.format(generalEnums.DATEFORMAT));
      return date;
    });
    setSelectedRange(false);
  };

  const handleNextCalendar = () => {
    setDateToShow((dateForward) => {
      const date = dateForward.add(1, 'month');
      dateForwardAction(date.format(generalEnums.DATEFORMAT));
      return date;
    });
    setSelectedRange(false);
  };

  const handleDateClick = (day) => {
    if (isDayDisabled(day)) return;
    if (isDisabled(day)) return;
    const date = dayjs(day);
    const isFirstHalf = date.date() <= 15;
    const startOfRange = isFirstHalf ? date.startOf('month') : date.startOf('month').add(15, 'day');
    const endOfRange = isFirstHalf ? date.startOf('month').add(14, 'day') : date.endOf('month');
    setSelectedRange({ start: startOfRange.toDate(), end: endOfRange.toDate() });
    setUserDate(day);
  };

  useEffect(() => {
    if (dateToShow.month() === dayjs().month() && userDate && dateDisablingAbility) {
      setDateToShow(userDate);
    }
    if (dayjs(currentDay).isSame(currentDay, 'date')) {
      handleDateClick(currentDay);
    }
    if (copyFrom) {
      handleDateClick(userDate);
    }
  }, []);

  return (
    <div className={classes.mainWrapper} style={{ backgroundColor, ...sx }}>
      <div className={classes.calendarHeaderWrapper}>
        <div className={classes.actionButtons}>
          <ArrowBackIosNewIcon fontSize="small" onClick={handlePreviousCalendar} sx={{ color: colors.LIGHT_GREY }} />
          <p>{dateToShow.clone().format('MMMM, YYYY')}</p>
          <ArrowForwardIosIcon fontSize="small" onClick={handleNextCalendar} sx={{ color: colors.LIGHT_GREY }} />
        </div>
      </div>
      <div className={classes.weekDaysWrapper}>
        {generateWeeksOfTheMonth[0].map((day, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className={classes.weekDayCell} key={`week-day-${index}`}>
            {dayjs(day).format('ddd')}
          </div>
        ))}
      </div>
      {generateWeeksOfTheMonth.map((week) => (
        <div key={`week-${week.toString()}`} className={classes.calendarBiMonthlyWrapper}>
          {week.map((day) => (
            <button
              key={`day-${day.toString()}`}
              onClick={() => handleDateClick(day)}
              className={`${classes.calendarDayCell} ${getClassForVariant(day)} ${
                isDisabled(day) || isDayDisabled(day) ? classes.disabled : ''
              } ${loading && classes.loadingText}`}
            >
              {day.getDate()}
            </button>
          ))}
        </div>
      ))}
      <div className={classes.biMonthlySelector}>
        <button
          onClick={() => handleDateClick(dateToShow.startOf('month').toDate())}
          className={selectedRange && selectedRange.start.getDate() === 1 ? classes.activeRange : ''}
        >
          1-15th
        </button>
        <button
          onClick={() => handleDateClick(dateToShow.startOf('month').add(15, 'day').toDate())}
          className={selectedRange && selectedRange.start.getDate() === 16 ? classes.activeRange : ''}
        >
          16-{dateToShow.endOf('month').date()}th
        </button>
      </div>
    </div>
  );
}

CustomBiMonthlyPicker.propTypes = {
  userDate: PropTypes.object,
  setUserDate: PropTypes.func,
  calendarType: PropTypes.oneOf(['day', 'week']),
  backgroundColor: PropTypes.string,
  disabledBeforeIncluded: PropTypes.bool
};
